import { formatChipsShort } from '@polf/shared/locale'
import { useState } from 'react'

import type {
  LeaderboardUnionFieldsFragment,
  PolfLeaderboardPlayerEntryFragment,
} from '../../__generated__/hooks'
import Header from './Header'
import Hero from './Hero'
import type { CourseHoleSummary } from './MiniScorecard'
import PolfDetail from './PolfDetail'
import PolfRow, { type PolfMode } from './PolfRow'
import Toggle from './Toggle'

type PolfLb = Extract<
  LeaderboardUnionFieldsFragment,
  { __typename: 'PolfLeaderboard' }
>

/**
 * POLF leaderboard — chip economy with a Chips / Stableford toggle. The
 * leader-gap line in the hero follows the active toggle, matching what
 * the player is reading on the rows. Tapping a row opens the PolfDetail
 * sheet with the chip-source breakdown + hole-by-hole scorecard.
 */
const PolfView = ({
  lb,
  youId,
  courseHoles,
}: {
  lb: PolfLb
  youId?: string
  courseHoles: readonly CourseHoleSummary[]
}) => {
  const [mode, setMode] = useState<PolfMode>('CHIPS')
  const [selected, setSelected] =
    useState<PolfLeaderboardPlayerEntryFragment | null>(null)
  const players = lb.players ?? []
  const you = youId ? players.find((p) => p.player.id === youId) ?? null : null
  const leader = players.find((p) => p.rank === 1)
  const leaderGap =
    you && leader
      ? mode === 'CHIPS'
        ? leader.chipsInclHoleCompetitions - you.chipsInclHoleCompetitions
        : leader.stablefordPoints.count - you.stablefordPoints.count
      : null
  return (
    <section className="pb-4">
      <Header
        controls={
          <Toggle
            options={[
              { value: 'CHIPS', label: 'Chips' },
              { value: 'STABLEFORD', label: 'Stableford' },
            ]}
            value={mode}
            onChange={setMode}
          />
        }
      />
      {you ? (
        <Hero
          yourRank={you.rank ?? null}
          yourPrimary={
            mode === 'CHIPS'
              ? formatChipsShort(you.chipsInclHoleCompetitions)
              : String(you.stablefordPoints.count)
          }
          primarySuffix={mode === 'CHIPS' ? 'chips' : 'pts'}
          leaderGap={leaderGap}
          totalPlayers={players.length}
        />
      ) : null}
      <ul className="bg-paper mx-2 rounded-lg shadow-sm overflow-hidden">
        {players.map((entry) => (
          <PolfRow
            key={entry.player.id}
            entry={entry}
            isYou={youId != null && entry.player.id === youId}
            mode={mode}
            onClick={() => setSelected(entry)}
          />
        ))}
      </ul>
      <PolfDetail
        open={selected != null}
        entry={selected}
        courseHoles={courseHoles}
        onClose={() => setSelected(null)}
      />
    </section>
  )
}

export default PolfView
